import { useCallback, useEffect, useState } from 'react';
import { ApiError, getRecycleBin, type DeletedFile } from '@/lib/api';

/**
 * The agency's recycle bin (FIL-11).
 *
 * **It takes no argument, and that is the point rather than an omission.** The
 * bin is per agency and spans every space, and the agency is resolved server-side
 * from the token — the client never names one (SEC-5). That is the whole
 * difference from `useFiles` and `useFolders`, which are keyed to a place.
 *
 * **This is not authorization.** What comes back is what the caller may see, and
 * each row's `canRestore` is what they may do with it. The client only renders
 * both.
 */

export type RecycleBinState =
  | { kind: 'loading' }
  /** An empty bin is a loaded state — nobody has deleted anything. */
  | { kind: 'loaded'; files: DeletedFile[]; truncated: boolean }
  /** `status` carried because 403 and 500 differ to the reader. `0` is network-level. */
  | { kind: 'error'; status: number };

export interface UseRecycleBinResult {
  state: RecycleBinState;
  /** After a failure. Clears back to `loading` first. */
  retry: () => void;
  /**
   * After a restore.
   *
   * Deliberately does **not** reset to `loading`: the rows on screen are still
   * true while the refetch is in flight, and blanking the table would make a
   * one-click action flicker.
   */
  refresh: () => void;
}

export function useRecycleBin(): UseRecycleBinResult {
  const [state, setState] = useState<RecycleBinState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getRecycleBin(controller.signal)
      .then(({ files, truncated }) => {
        setState({ kind: 'loaded', files, truncated });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, refresh };
}
